import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { COLORS, bodyCellSx } from './storeManagementConstants';
import {
  KitchenAvatar,
  getOnboardingBoolDisplay,
  getStatusDisplay,
  getStoreVisibilityDisplay,
} from './storeManagementParts';

export function StoreManagementTableRow({ row, index, openMenu, onViewStore }) {
  return (
    <TableRow
      hover
      sx={{
        transition: 'background-color 0.15s ease',
        bgcolor: index % 2 === 1 ? 'rgba(248,249,251,0.8)' : 'transparent',
        '&:hover': {
          bgcolor: 'rgba(255, 123, 41, 0.04) !important',
          '& .kitchen-accent-cell': {
            borderLeft: `3px solid ${COLORS.accentOrange}`,
          },
        },
        '&:last-child td': { borderBottom: 'none' },
      }}
    >
      <TableCell
        className="kitchen-accent-cell"
        sx={{
          ...bodyCellSx,
          borderLeft: '3px solid transparent',
          transition: 'border-left 0.15s ease',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KitchenAvatar name={row.kitchenName} size={30} radius="8px" />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="button"
              type="button"
              onClick={() => onViewStore?.(row)}
              sx={{
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
                fontSize: 13,
                color: COLORS.ink,
                lineHeight: 1.3,
                p: 0,
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                transition: 'color 0.15s ease',
                '&:hover': {
                  color: COLORS.accentOrange,
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                },
              }}
            >
              {row.kitchenName}
            </Typography>
            {row.kitchenDetail ? (
              <Typography
                sx={{
                  fontFamily: 'Inter Regular, sans-serif',
                  fontSize: 11,
                  color: COLORS.muted,
                  lineHeight: 1.3,
                  mt: 0.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.kitchenDetail}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </TableCell>

      <TableCell sx={bodyCellSx}>
        <Typography
          component={RouterLink}
          to={`/products?kitchenName=${encodeURIComponent(row.kitchenName)}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 34,
            px: 1.25,
            py: 0.4,
            bgcolor: 'rgba(255, 123, 41, 0.10)',
            color: COLORS.accentOrange,
            border: `1px solid rgba(255, 123, 41, 0.25)`,
            borderRadius: '8px',
            fontFamily: 'Inter Bold, sans-serif',
            fontSize: 13,
            fontWeight: 700,
            textDecoration: 'none',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: COLORS.accentOrange,
              color: COLORS.white,
              borderColor: COLORS.accentOrange,
            },
          }}
        >
          {row.products}
        </Typography>
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, overflow: 'hidden' }}>
        <Typography
          sx={{
            fontFamily: 'Inter Regular, sans-serif',
            fontSize: 12,
            color: COLORS.muted,
            lineHeight: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.zipcode || '—'}
        </Typography>
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, overflow: 'hidden' }}>
        {getStoreVisibilityDisplay(row.storeVisibilityStatus)}
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, overflow: 'hidden' }}>
        {getStatusDisplay(row.status) ?? (
          <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>—</Typography>
        )}
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{getOnboardingBoolDisplay(row.einMenuUploaded)}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{getOnboardingBoolDisplay(row.menuVerified)}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{getOnboardingBoolDisplay(row.deviceAssigned)}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
        {getOnboardingBoolDisplay(row.dedicatedManagerAssigned)}
      </TableCell>

      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }} align="center">
        <IconButton
          size="small"
          aria-label="row actions"
          onClick={openMenu(row?.id)}
          sx={{
            color: COLORS.muted,
            borderRadius: '8px',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: 'rgba(63, 63, 110, 0.08)',
              color: COLORS.ink,
            },
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
